import React, { useMemo, useState } from 'react';

import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ThreeDot from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';

import NestedQMenu from 'components/NestedQMenu';

import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';

import Dump from 'components/Dump';

import { makeStyles } from 'tss-react/mui';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { useAppSelector } from 'hooks/redux';
import AccountSource from './reviewConditions/AccountSource';
import AmountSource from './reviewConditions/AmountSource';
import CategoryTarget from './reviewConditions/CategoryTarget';
import ExcludedTarget from './reviewConditions/ExcludedTarget';
import { SourceHeading, TargetHeading } from './reviewConditions/Headings';
import MemoTarget from './reviewConditions/MemoTarget';
import PayeeSource from './reviewConditions/PayeeSource';
import PayeeTarget from './reviewConditions/PayeeTarget';
import ReviewedTarget from './reviewConditions/ReviewedTarget';
import TagsTarget from './reviewConditions/TagsTarget';

const useStyles = makeStyles()((theme) => ({
  background: {
    background: theme.palette.background.default,
  },
  row: {
    display: 'flex',
    flex: 1,
    flexWrap: 'wrap',
    '> *': {
      lineHeight: '24px',
    },

    '>.MuiBox-root+.MuiBox-root': {
      padding: `${theme.spacing(3)} ${theme.spacing(2)}`,
    },
    alignContent: 'flex-start',
    alignItems: 'center',
  },
  conditionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    '> div+div': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
  },
  column: {
    display: 'flex',
    flex: '0 0 50%',
    overflow: 'hidden',
    flexDirection: 'column',
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
    gap: theme.spacing(1),
  },
  columnRow: {
    display: 'flex',
  },
  threeDotMenu: {
    height: 24,
    color: theme.palette.icon.light,
    alignSelf: 'center',
    padding: 0,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  columnMain: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
    gap: theme.spacing(1),
  },
  threeDotArea: {
    display: 'flex',
    flex: 0,
    alignSelf: 'flex-start',
  },
  rounded: {
    width: '100%',
    borderRadius: 20,
    border: `1px solid ${theme.palette.divider}`,
    '>*': {
      border: 'none',
    },
    '>:first-of-type': {
      borderTopRightRadius: 20,
      borderTopLeftRadius: 20,
    },
  },
}));

type RuleSummaryProps = {
  rule?: TransactionRuleType;
  onEditCallback?: (id: string) => void;
  onDeleteCallback?: (id: string) => void;
  onDuplicateCallback?: (id: string) => void;
  veritcalWithLabel?: boolean;
};

const RuleSummaryRow = ({
  rule,
  onEditCallback,
  onDeleteCallback,
  onDuplicateCallback,
  veritcalWithLabel,
}: RuleSummaryProps) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const advanceRuleConditions = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'txnRulesAdvanced'),
  );

  const [anchorEl, setAnchorEl] = useState(null);

  const menuOptions = useMemo(() => {
    const options = [];
    if (rule) {
      if (onEditCallback) {
        options.push({
          id: 'edit-rule',
          label: 'Edit',
          startIcon: <EditIcon />,
          onSelect: () => {
            setAnchorEl(null);
            rule.id && onEditCallback(rule.id);
          },
        });
      }
      if (onDuplicateCallback) {
        options.push({
          id: 'duplicate-rule',
          label: 'Duplicate',
          startIcon: <ControlPointDuplicateIcon />,
          onSelect: () => {
            setAnchorEl(null);
            rule.id && onDuplicateCallback(rule.id);
          },
        });
      }
      if (onDeleteCallback) {
        options.push({
          id: 'delete-rule',
          label: 'Delete',
          onSelect: () => {
            setAnchorEl(null);
            rule.id && onDeleteCallback(rule.id);
          },
          customRender: (
            <Typography
              variant='bodyRegular'
              style={{ color: theme.palette.error.main, display: 'flex', alignItems: 'center' }}
            >
              <DeleteIcon style={{ marginRight: theme.spacing(1) }} />
              Delete
            </Typography>
          ),
        });
      }
    }
    return options;
  }, [theme, rule, onEditCallback, onDuplicateCallback, onDeleteCallback]);

  return (
    <>
      <NestedQMenu
        id='txn-rules-three-dot-menu'
        key={`three-dot`}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        options={menuOptions}
      />
      {rule && (
        <Box
          className={cx(
            veritcalWithLabel ? (classes.column, classes.rounded) : (classes.row, classes.conditionsContainer),
          )}
        >
          <Dump obj={rule} />
          {veritcalWithLabel && <SourceHeading className={cx(veritcalWithLabel ? classes.background : '')} />}
          <Box className={classes.column}>
            <PayeeSource payee={rule.payeeSource} />
            {advanceRuleConditions && <AccountSource account={rule.accountSource} />}
            {advanceRuleConditions && <AmountSource amount={rule.amountSource} />}
          </Box>

          {veritcalWithLabel && <TargetHeading className={cx(veritcalWithLabel ? classes.background : '')} />}
          <Box className={classes.column}>
            <Box className={classes.columnRow}>
              <Box className={classes.columnMain}>
                <PayeeTarget payeeTarget={rule.payeeTarget} />
                <CategoryTarget
                  coaTarget={rule.coaTarget}
                  businessCoaTarget={rule.businessCoaTarget}
                  useDefaultAccount={!!rule.useDefaultAccountUsage}
                />
                {advanceRuleConditions && <TagsTarget tagTargetIds={rule.tagsTarget || []} />}
                {advanceRuleConditions && <MemoTarget memoTarget={rule.memoTarget} />}
                {advanceRuleConditions && <ExcludedTarget excludedTarget={rule.excludedTarget} />}
                {advanceRuleConditions && <ReviewedTarget reviewedTarget={rule.reviewedTarget} />}
              </Box>

              <Box className={classes.threeDotArea}>
                {menuOptions.length > 0 && (
                  <IconButton
                    id='rules-list-three-dot-menu'
                    className={classes.threeDotMenu}
                    aria-label='three-dot-menu'
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setAnchorEl(e.currentTarget);
                    }}
                  >
                    <ThreeDot />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default RuleSummaryRow;
